import Modal from 'components/common/modal';
import { useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react';
import { Button } from 'antd';
import { css, Global } from '@emotion/react';
import useStores from 'hooks/use-stores.hooks';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'types/theme.types';
import { useHasPermission } from 'hooks/use-has-permission';
import { useCurrentOrganizationId } from 'hooks/use-current-organization-id';
import { links } from 'constants/external-links';
import { isMonthlyRunsPayGateError, getMonthlyRunsResetDate } from 'utils/pay-gate.utils';
import { legacyOnly } from 'constants/themes.constants';

const PAY_GATE_MODAL_ROOT_CLASS = 'pay-gate-modal-root';

/**
 * Figma's backdrop is a flat grey scrim with no blur, and the paywall is not a designed exception: every
 * overlay now takes `colorBgMask` from the one DS backdrop token (APO-462). A `<Global>` block is
 * design-system drift with no owner, so the new design emits nothing here at all.
 *
 * Exported because a `<Global>` has no element to scope a rule lookup to - the spec asserts the branch
 * itself. APO-484 deletes this with every other `legacyOnly`.
 */
export const payGateGlobalStyles = legacyOnly(
  () => css`
    .${PAY_GATE_MODAL_ROOT_CLASS} .ant-modal-mask {
      backdrop-filter: blur(4px);
    }
  `
);

const PayGate = () => {
  const { payGateStore } = useStores();
  const organizationId = useCurrentOrganizationId();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthorized: canManageBilling } = useHasPermission('MANAGE_BILLING');
  const route = location.pathname + location.search;

  useEffect(() => {
    payGateStore.checkCachedRoute(route);
  }, [payGateStore, route]);

  const onClose = useCallback(() => {
    payGateStore.clearActivePayGateError();
  }, [payGateStore]);

  const onContactUsClick = useCallback(() => {
    onClose();
    navigate(`/organizations/${organizationId}/billing`);
  }, [navigate, onClose, organizationId]);

  const isRunsCap = isMonthlyRunsPayGateError(payGateStore.activePayGateErrorText);

  return (
    <>
      {/* `Global` hands the theme straight to its `styles` function, where a styled interpolation gets
          the whole props object - so the branch helper is adapted rather than passed through. */}
      <Global styles={theme => payGateGlobalStyles({ theme })} />
      <ModalWithLineBreaks
        open={!!payGateStore.activePayGateErrorText}
        size="large"
        width={600}
        rootClassName={PAY_GATE_MODAL_ROOT_CLASS}
        onCancel={onClose}
        titleId={isRunsCap ? 'paygate.runs.title' : 'pay-gate.title'}
        destroyOnClose
        maskClosable
        centered
        footer={
          isRunsCap ? (
            <PayGateFooter>
              <ContactSalesLink href={links.BILLING.CONTACT_US} target="_blank" rel="noopener noreferrer">
                <FormattedMessage id="paygate.runs.contact-sales" />
              </ContactSalesLink>
              {canManageBilling && (
                <Button type="primary" href={links.BILLING.CONTACT_US} target="_blank" rel="noopener noreferrer">
                  <FormattedMessage id="paygate.explore-tier-upgrade" />
                </Button>
              )}
            </PayGateFooter>
          ) : canManageBilling ? (
            <Button type="primary" onClick={onContactUsClick}>
              <FormattedMessage id="paygate.explore-tier-upgrade" />
            </Button>
          ) : null
        }>
        {isRunsCap ? (
          <div>
            <FormattedMessage id="paygate.runs.message" values={{ resetDate: getMonthlyRunsResetDate() }} />
            {!canManageBilling && (
              <div>
                <FormattedMessage id="paygate.contact-admin" />
              </div>
            )}
          </div>
        ) : (
          <>
            <div>{payGateStore.activePayGateErrorText}</div>
            {!canManageBilling && <FormattedMessage id="paygate.contact-admin" />}
          </>
        )}
      </ModalWithLineBreaks>
    </>
  );
};

const ModalWithLineBreaks = styled(Modal)`
  white-space: pre-line;
`;

const PayGateFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ContactSalesLink = styled.a`
  font-weight: 500;
`;

export default observer(PayGate);
